import React, { forwardRef, memo } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { isSafari } from '@atlassian/jira-common-util-browser/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { AssigneeInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-assignee-inline-edit-full/src/ui/assignee/index.tsx';
import { ReporterInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-reporter-inline-edit-full/src/ui/reporter/index.tsx';
import { SingleUserPickerInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-single-user-picker-inline-edit-full/src/ui/single-user-picker/index.tsx';
import { toProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { DUPLICATE_FIELD_EDIT_WIDTH } from '../../../constants.tsx';
import type { UserCellInternal_UserCellInternal$key } from './__generated__/UserCellInternal_UserCellInternal.graphql';
import type { UserCellInternal$key } from './__generated__/UserCellInternal.graphql';
import messages from './messages.tsx';

export type Props = {
	fieldRef: UserCellInternal$key;
	projectKey: string;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
	isColumnSticky?: boolean;
};

export const UserCell = memo<Props>(
	({ fieldRef, projectKey, isFirstField, hasDuplicates, isColumnSticky }) => {
		const data = useFragment<UserCellInternal$key>(
			graphql`
				fragment UserCellInternal on JiraSingleSelectUserPickerField
				@argumentDefinitions(
					isInlineEditingEnabled: { type: "Boolean!" }
					isEditableSyncLoaded: { type: "Boolean!" }
				) {
					...UserCellInternal_UserCellInternal
						@arguments(
							isInlineEditingEnabled: $isInlineEditingEnabled
							isEditableSyncLoaded: $isEditableSyncLoaded
						)
				}
			`,
			fieldRef,
		);

		return (
			<UserCellInternal
				fieldRef={data}
				projectKey={projectKey}
				menuPortalTarget={
					// Portal the menu outside of the scroll container, i.e., table body
					// to prevent clipping in Frozen Columns or Safari (see https://bugs.webkit.org/show_bug.cgi?id=160953#c20)
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					isSafari() || isColumnSticky ? document.body : undefined
				}
				isFirstField={isFirstField}
				hasDuplicates={hasDuplicates}
			/>
		);
	},
);

type UserCellInternalProps = {
	fieldRef: UserCellInternal_UserCellInternal$key;
	projectKey: string;
	/** Whether the menu should use a portal, and where it should attach */
	menuPortalTarget?: HTMLElement;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

export const UserCellInternal = ({
	fieldRef,
	projectKey,
	menuPortalTarget,
	isFirstField,
	hasDuplicates,
}: UserCellInternalProps) => {
	const { formatMessage } = useIntl();
	const data = useFragment<UserCellInternal_UserCellInternal$key>(
		graphql`
			fragment UserCellInternal_UserCellInternal on JiraSingleSelectUserPickerField
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
			) {
				id
				fieldId
				isEditableInIssueView @include(if: $isEditableSyncLoaded)
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled) # defined in ../../../graphql/schema.graphql
				...assignee_issueFieldAssigneeInlineEditFull_AssigneeInlineEditViewWithIsEditable_fragmentRef
				...reporter_issueFieldReporterInlineEditFull_ReporterInlineEditViewWithIsEditable_fragmentRef
				...singleUserPicker_issueFieldSingleUserPickerInlineEditFull_SingleUserPickerInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isAssigneeReporter = data.fieldId === 'assignee' || data.fieldId === 'reporter';
	const isCreatorField = data.fieldId === 'creator';

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	const isInlineEditingSupported = isAssigneeReporter || isInlineEditingExtendedFieldSupportEnabled;

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	// We can also disable editability of the creator field as it is never editable.
	let isEditable: boolean | null | undefined;
	if ('isEditableInIssueView' in data) {
		isEditable = data.isEditableInIssueView;
	} else {
		isEditable =
			'lazyIsEditableInIssueView' in data && isInlineEditingSupported && !isCreatorField
				? data.lazyIsEditableInIssueView
				: false;
	}
	useLazyLoadEditability(data.id, isEditable);

	const shouldRenderHoverPopover = useIsSingleLineRowHeightEnabled();

	if (hasDuplicates && shouldRenderHoverPopover) {
		switch (data.fieldId) {
			case 'assignee': {
				return (
					<AssigneeInlineEditViewWithIsEditable
						attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
						spacing="default"
						editViewPopup
						editViewPopupAlignBlock="start"
						editViewPopupWidth={DUPLICATE_FIELD_EDIT_WIDTH}
						// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
						editViewPopupTarget={isFirstField ? CustomTarget : undefined}
						fragmentRef={data}
						isEditable={isEditable ?? false}
						menuPosition={isFirstField ? 'fixed' : 'absolute'}
						portalElement={menuPortalTarget}
						readViewFitContainerHeight
						projectKey={toProjectKey(projectKey)}
						shouldRenderHoverPopover={shouldRenderHoverPopover}
					/>
				);
			}
			case 'reporter': {
				return (
					<ReporterInlineEditViewWithIsEditable
						attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
						spacing="default"
						editViewPopup
						editViewPopupAlignBlock="start"
						editViewPopupWidth={DUPLICATE_FIELD_EDIT_WIDTH}
						// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
						editViewPopupTarget={isFirstField ? CustomTarget : undefined}
						fragmentRef={data}
						isEditable={isEditable ?? false}
						menuPosition={isFirstField ? 'fixed' : 'absolute'}
						portalElement={menuPortalTarget}
						readViewFitContainerHeight
						shouldRenderHoverPopover={shouldRenderHoverPopover}
					/>
				);
			}
			default: {
				const noneOptionLabel = isCreatorField ? formatMessage(messages.anonymous) : undefined;
				return (
					<SingleUserPickerInlineEditViewWithIsEditable
						attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
						spacing="default"
						editViewPopup
						editViewPopupAlignBlock="start"
						editViewPopupWidth={DUPLICATE_FIELD_EDIT_WIDTH}
						// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
						editViewPopupTarget={isFirstField ? CustomTarget : undefined}
						fragmentRef={data}
						isEditable={isEditable ?? false}
						menuPosition={isFirstField ? 'fixed' : 'absolute'}
						portalElement={menuPortalTarget}
						readViewFitContainerHeight
						noneOptionLabel={noneOptionLabel}
						shouldRenderHoverPopover={shouldRenderHoverPopover}
					/>
				);
			}
		}
	}

	switch (data.fieldId) {
		case 'assignee': {
			return (
				<AssigneeInlineEditViewWithIsEditable
					attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
					spacing="default"
					editViewPopup
					editViewPopupAlignBlock="center"
					fragmentRef={data}
					isEditable={isEditable ?? false}
					menuPosition="fixed"
					portalElement={menuPortalTarget}
					readViewFitContainerHeight
					projectKey={toProjectKey(projectKey)}
					shouldRenderHoverPopover={shouldRenderHoverPopover}
				/>
			);
		}
		case 'reporter': {
			return (
				<ReporterInlineEditViewWithIsEditable
					attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
					spacing="default"
					editViewPopup
					editViewPopupAlignBlock="center"
					fragmentRef={data}
					isEditable={isEditable ?? false}
					menuPosition="fixed"
					portalElement={menuPortalTarget}
					readViewFitContainerHeight
					shouldRenderHoverPopover={shouldRenderHoverPopover}
				/>
			);
		}
		default: {
			const noneOptionLabel = isCreatorField ? formatMessage(messages.anonymous) : undefined;
			return (
				<SingleUserPickerInlineEditViewWithIsEditable
					attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
					spacing="default"
					editViewPopup
					editViewPopupAlignBlock="center"
					fragmentRef={data}
					menuPosition="fixed"
					isEditable={isEditable ?? false}
					portalElement={menuPortalTarget}
					readViewFitContainerHeight
					noneOptionLabel={noneOptionLabel}
					shouldRenderHoverPopover={shouldRenderHoverPopover}
				/>
			);
		}
	}
};

const CustomTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return (
		<Box
			xcss={fg('nin_issue_table_rtl_fix') ? customTargetStyles : customTargetStylesOld}
			ref={ref}
		/>
	);
});

const customTargetStylesOld = xcss({
	width: 'space.0',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	top: 'space.0',
});

const customTargetStyles = xcss({
	width: 'space.0',
	position: 'absolute',
	insetBlockStart: 'space.0',
});
